// StaffManagementTab.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Paper,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { PersonRemove, Add, History } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import { QK } from "../../base/qk";
import { unassignEmployees } from "../../api/services/scheduling";
import EmployeeHistoryModal from "../ClientsScreen/EmployeeHistoryModal";
import AddScheduleEmployee from "../../components/ScheduleJob/EmployeeAssignment/AddScheduleEmployee";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const StaffManagementTab = ({ jobDetails, isLoading, area, jobId, assignmentId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [historyModalEmployee, setHistoryModalEmployee] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [employeeToUnassign, setEmployeeToUnassign] = useState(null);

  const currentEmployees = jobDetails?.employee_details || [];

  const handleUnassign = async () => {
    if (!employeeToUnassign) return;
    
    setIsUnassigning(true);
    try {
      const response = await unassignEmployees({
        schedule_job_id: jobId,
        employee_ids: [employeeToUnassign],
      });
      if (response.status === 200) {
        enqueueSnackbar("Staff unassigned successfully", { variant: "success" });
        queryClient.invalidateQueries(["job-details", jobId]);
        queryClient.invalidateQueries([QK.assignmentDetails]);
        queryClient.invalidateQueries([QK.scheduleTimeline]);
        setShowConfirmModal(false);
        setEmployeeToUnassign(null);
      }
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || "Failed to unassign staff", { variant: "error" });
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleUnassignClick = (employeeId) => {
    setEmployeeToUnassign(employeeId);
    setShowConfirmModal(true);
  };

  const handleShowHistory = (employeeId) => {
    setHistoryModalEmployee({ id: employeeId });
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setHistoryModalEmployee(null);
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setEmployeeToUnassign(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const employeeToRemove = currentEmployees.find(
    (emp) => emp.id === employeeToUnassign
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
          Staff Management
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={() => setShowAddEmployee(true)}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: "#2469F0",
            "&:hover": {
              bgcolor: "#1A5BD4",
            },
          }}
        >
          Add Staff
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          borderColor: "#E2E8F0",
          bgcolor: "#F8FAFC",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="caption" fontWeight={600} color="#64748B">
            Currently Assigned Staff
          </Typography>
          <Chip
            label={`${currentEmployees.length} staff`}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            }}
          />
        </Box>

        {currentEmployees.length > 0 ? (
          <Stack spacing={1.5}>
            {currentEmployees.map((emp) => (
              <Box
                key={emp.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  bgcolor: "white",
                  borderRadius: 2,
                  border: "1px solid #E2E8F0",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar
                    src={emp.profile_photo || ""}
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {!emp.profile_photo && `${emp.first_name?.[0]}${emp.last_name?.[0]}`}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {emp.first_name} {emp.last_name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="#94A3B8">
                        ID: {emp.id}
                      </Typography>
                      {emp.actual_start && (
                        <Chip
                          label="Started"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.5rem",
                            bgcolor: alpha(theme.palette.success.main, 0.12),
                            color: theme.palette.success.main,
                          }}
                        />
                      )}
                      {emp.actual_end && (
                        <Chip
                          label="Completed"
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.5rem",
                            bgcolor: alpha(theme.palette.info.main, 0.12),
                            color: theme.palette.info.main,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="View History" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleShowHistory(emp.id)}
                      sx={{
                        color: "#94A3B8",
                        "&:hover": {
                          color: "#2469F0",
                          bgcolor: alpha("#2469F0", 0.06),
                        },
                      }}
                    >
                      <History fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Unassign" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleUnassignClick(emp.id)}
                      disabled={isUnassigning || !!emp.actual_start}
                      sx={{
                        color: emp.actual_start ? "#94A3B8" : "#EF4444",
                        "&:hover": {
                          bgcolor: emp.actual_start ? "transparent" : alpha("#EF4444", 0.08),
                        },
                      }}
                    >
                      <PersonRemove fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              py: 3,
              textAlign: "center",
              bgcolor: "white",
              borderRadius: 2,
              border: "1px dashed #E2E8F0",
            }}
          >
            <Typography variant="body2" color="#94A3B8">
              No staff assigned to this job
            </Typography>
            <Typography variant="caption" color="#CBD5E1">
              Click "Add Staff" to assign employees
            </Typography>
          </Box>
        )}
      </Paper>

      <AddScheduleEmployee
        show={showAddEmployee}
        handleClose={() => setShowAddEmployee(false)}
        area={area}
        jobId={jobId}
        assignmentId={assignmentId}
        jobDetails={jobDetails}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={handleCloseConfirmModal}
        handleConfirm={handleUnassign}
        isLoading={isUnassigning}
        title="Unassign Staff"
        body={`Are you sure you want to unassign ${employeeToRemove?.first_name || ''} ${employeeToRemove?.last_name || ''} from this job?`}
        confirmText="Yes, Unassign"
        cancelText="Cancel"
        isDelete="true"
      />

      {historyModalEmployee && (
        <EmployeeHistoryModal
          show={showHistoryModal}
          handleClose={handleCloseHistoryModal}
          employeeId={historyModalEmployee.id}
        />
      )}
    </Box>
  );
};

export default StaffManagementTab;